import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, RefreshCw, Search, LayoutGrid, List, Pencil, Eye, Loader2, Trash2, CalendarDays } from 'lucide-react';
import { Sidebar } from '../../components/Sidebar';
import { caApi, errMsg, dateText, BTN_PRIMARY, BTN_OUTLINE, CARD, Avatar, Handle } from './caShared';
import { CompetitorModal } from './CompetitorModal';

const Status = ({ c }) => {
  if (c.scrape_status === 'running') return <span className="inline-flex items-center gap-1 text-xs font-medium text-[#3B4FD8]"><Loader2 size={12} className="animate-spin" /> Pulling data…</span>;
  if (c.scrape_status === 'error') return <span className="text-xs font-medium text-red-600">Last pull failed</span>;
  return c.post_count ? <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Data available</span>
    : <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9CA3AF]"><span className="h-1.5 w-1.5 rounded-full bg-gray-300" />No data yet</span>;
};

const CompetitorCard = ({ c, grid, onEdit, onDelete }) => (
  <div className={`${CARD} p-5 flex ${grid ? 'flex-col gap-4' : 'flex-col md:flex-row md:items-center gap-4 md:gap-6'} hover:shadow-[0_4px_14px_rgba(59,79,216,0.10)] transition-shadow`} data-testid={`competitor-card-${c.id}`}>
    <div className="flex items-center gap-3 md:min-w-[260px]">
      <Avatar name={c.name} size={44} />
      <div className="min-w-0"><div className="font-semibold text-[#111827] dark:text-white truncate">{c.name}</div><Handle h={c.instagram_handle} /></div>
    </div>
    <div className="flex flex-wrap gap-1.5 flex-1">
      <span className="rounded-full px-2.5 py-1 text-xs font-medium bg-[#3B4FD8]/10 text-[#3B4FD8]">{c.category}</span>
      {c.sub_categories.map((s) => <span key={s} className="rounded-full px-2.5 py-1 text-xs bg-[#F3F4F8] dark:bg-white/5 text-[#4B5563] dark:text-zinc-300">{s}</span>)}
      {c.brand_name && <span className="rounded-full px-2.5 py-1 text-xs bg-amber-50 text-amber-700">for {c.brand_name}</span>}
    </div>
    <div className="text-xs text-[#6B7280] md:w-44 space-y-1">
      <div className="inline-flex items-center gap-1"><CalendarDays size={12} /> Last updated {dateText(c.last_scraped_at)}</div>
      <div><Status c={c} /></div>
    </div>
    <div className="flex items-center gap-2">
      <button onClick={() => onDelete(c)} title="Delete" className="p-2 rounded-lg text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`competitor-delete-${c.id}`}><Trash2 size={15} /></button>
      <button onClick={() => onEdit(c)} className={BTN_OUTLINE} data-testid={`competitor-edit-${c.id}`}><Pencil size={14} /> Edit</button>
      <Link to={`/competitor-analysis/${c.id}`} className={BTN_PRIMARY} data-testid={`competitor-view-${c.id}`}><Eye size={14} /> View Report</Link>
    </div>
  </div>
);

export default function CompetitorList() {
  const [list, setList] = useState(null);
  const [options, setOptions] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [grid, setGrid] = useState(false);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => caApi.get('').then((d) => {
    setList(d.competitors);
    clearTimeout(timer.current);
    if (d.competitors.some((c) => c.scrape_status === 'running')) timer.current = setTimeout(() => load(), 1500);
  }).catch((e) => { setList([]); toast.error(errMsg(e, 'Could not load competitors')); }), []);
  useEffect(() => { load(); caApi.get('/options').then(setOptions).catch(() => {}); return () => clearTimeout(timer.current); }, [load]);

  const refreshAll = async () => {
    setBusy(true);
    try { const r = await caApi.post('/scrape-all'); toast.success(`Refreshing ${r.started} competitors`, { description: 'Apify integration pending — stub data only.' }); load(); } catch (e) { toast.error(errMsg(e, 'Refresh failed')); } finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name} and all its tracked posts?`)) return;
    try { await caApi.del(`/${c.id}`); toast.success(`${c.name} deleted`); load(); } catch (e) { toast.error(errMsg(e, 'Could not delete')); }
  };
  const term = q.trim().toLowerCase().replace(/^@/, '');
  const shown = (list || []).filter((c) => (!term || c.name.toLowerCase().includes(term) || c.instagram_handle.includes(term)) && (!cat || c.category === cat));

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-[#F7F8FC] dark:bg-[#0A0B0D] min-w-0" data-testid="competitor-analysis-page">
        <div className="max-w-[1300px] mx-auto space-y-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight text-[#111827] dark:text-white">Competitor Analysis</h1>
            <div className="flex gap-2">
              <button onClick={refreshAll} disabled={busy || !list?.length} className={BTN_OUTLINE} data-testid="competitor-refresh-all-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh All</button>
              <button onClick={() => setModal({ open: true, editing: null })} className={BTN_PRIMARY} data-testid="competitor-add-btn"><Plus size={15} /> Add Competitor</button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="relative w-full sm:w-96"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by handle or name" className="w-full h-10 rounded-lg pl-9 pr-3 text-sm bg-white dark:bg-[#1A1D24] border border-[#D9DCE6] dark:border-white/10 focus:outline-none focus:border-[#3B4FD8] transition-colors" data-testid="competitor-search-input" /></div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-[#D9DCE6] overflow-hidden bg-white dark:bg-transparent">
                {[[false, List, 'list'], [true, LayoutGrid, 'grid']].map(([g, Icon, k]) => <button key={k} onClick={() => setGrid(g)} className={`p-2 transition-colors ${grid === g ? 'bg-[#3B4FD8] text-white' : 'text-[#6B7280] hover:text-[#3B4FD8]'}`} data-testid={`competitor-view-${k}`}><Icon size={15} /></button>)}
              </div>
              <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 rounded-lg px-3 text-sm bg-white dark:bg-[#1A1D24] border border-[#D9DCE6] focus:outline-none focus:border-[#3B4FD8]" data-testid="competitor-filter-select">
                <option value="">Filter By: All categories</option>{(options?.categories || []).map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {list === null ? <Loader2 className="animate-spin text-[#3B4FD8]" size={20} /> : !shown.length
            ? <div className={`${CARD} p-10 text-center text-sm text-[#6B7280]`} data-testid="competitor-empty">{list.length ? 'No competitors match your search.' : 'No competitors yet — click "Add Competitor" to start tracking one.'}</div>
            : <div className={grid ? 'grid sm:grid-cols-2 xl:grid-cols-3 gap-4' : 'space-y-3'}>{shown.map((c) => <CompetitorCard key={c.id} c={c} grid={grid} onEdit={(x) => setModal({ open: true, editing: x })} onDelete={remove} />)}</div>}
        </div>
      </div>
      <CompetitorModal open={modal.open} editing={modal.editing} options={options} onOpenChange={(o) => setModal((m) => ({ ...m, open: o }))} onSaved={load} />
    </div>
  );
}
